import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase.config';
import '../../styles/kyro-design.css';

const cInfo = [
  { icon: '📍', l: 'Office Address', v: '303, Unity One, Rajiv Gandhi Square\nIndore – 452001, Madhya Pradesh' },
  { icon: '📞', l: 'Phone', v: '+91 76 111 111 02' },
  { icon: '✉', l: 'Email', v: 'compliance@kyrocapital.in' },
];

const Contact: React.FC = () => {
  const [tab, setTab] = useState<'touch' | 'careers'>('touch');
  const [sent, setSent] = useState({ touch: false, careers: false });
  const [f, setF] = useState({
    name: '',
    org: '',
    phone: '',
    email: '',
    msg: '',
    cname: '',
    cemail: '',
    cphone: '',
    crole: '',
  });
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [careerSubmitting, setCareerSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitTouch = async () => {
    setSubmitError(null);
    try {
      await addDoc(collection(db, 'contactInquiries'), {
        name: f.name,
        organisation: f.org,
        phone: f.phone,
        email: f.email,
        message: f.msg,
        timestamp: serverTimestamp(),
        status: 'new',
      });
      setSent({ ...sent, touch: true });
    } catch (err) {
      console.error(err);
      setSubmitError('Could not send. Please try again or contact us directly.');
    }
  };

  const submitCareers = async () => {
    if (!f.cname.trim() || !f.cemail.trim()) return;
    setCareerSubmitting(true);
    setSubmitError(null);
    try {
      let resumeURL = '';
      if (resumeFile && resumeFile.size > 0) {
        const ext = resumeFile.name.split('.').pop()?.toLowerCase() || 'pdf';
        const resumeRef = ref(storage, `resumes/${Date.now()}_${f.cemail.replace(/[^a-z0-9]/gi, '_')}.${ext}`);
        await uploadBytes(resumeRef, resumeFile);
        resumeURL = await getDownloadURL(resumeRef);
      }
      await addDoc(collection(db, 'careerApplications'), {
        name: f.cname.trim(),
        email: f.cemail.trim(),
        phone: f.cphone.trim(),
        role: f.crole.trim() || null,
        resumeURL: resumeURL || null,
        timestamp: serverTimestamp(),
        status: 'new',
      });
      setSent({ ...sent, careers: true });
      setResumeFile(null);
    } catch (err) {
      console.error(err);
      setSubmitError('Could not submit. Please try again or contact us directly.');
    } finally {
      setCareerSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="contact-layout">
        <div className="contact-left">
          <div className="contact-bg" />
          <div className="c-inner">
            <div className="eyebrow eyebrow-lt">Contact Us</div>
            <div className="sec-title sec-title-lt" style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.1rem)', marginBottom: '1rem' }}>
              Let's Start<br />a Conversation.
            </div>
            <p style={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
              Whether you are looking to invest, explore an advisory mandate or join our team — we would be glad to hear from you.
            </p>
            <div className="c-info">
              {cInfo.map((c) => (
                <div className="c-item" key={c.l}>
                  <div className="c-icon">{c.icon}</div>
                  <div>
                    <div className="c-lbl">{c.l}</div>
                    <div className="c-val" style={{ whiteSpace: 'pre-line' }}>{c.v}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="contact-right">
          <div className="c-tabs">
            <button className={`c-tab ${tab === 'touch' ? 'active' : ''}`} onClick={() => { setTab('touch'); setSubmitError(null); }}>
              Get in Touch
            </button>
            <button className={`c-tab ${tab === 'careers' ? 'active' : ''}`} onClick={() => { setTab('careers'); setSubmitError(null); }}>
              Careers
            </button>
          </div>

          {tab === 'touch' && (
            sent.touch ? (
              <div style={{ padding: '2rem', background: 'rgba(16,140,88,0.07)', border: '1px solid rgba(16,140,88,0.25)', fontSize: '0.9rem', color: 'var(--navy)', lineHeight: 1.7 }} role="status" aria-live="polite">
                ✓ Message received. We'll be in touch at <strong>{f.email}</strong>.
              </div>
            ) : (
              <div className="c-form">
                <div className="c-2col">
                  <input className="c-input" placeholder="Full Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                  <input className="c-input" placeholder="Organisation" value={f.org} onChange={(e) => setF({ ...f, org: e.target.value })} />
                </div>
                <input className="c-input" type="tel" placeholder="Contact Number" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
                <input className="c-input" type="email" placeholder="Email Address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                <textarea className="c-textarea" placeholder="Your Message" value={f.msg} onChange={(e) => setF({ ...f, msg: e.target.value })} />
                {submitError && <p style={{ fontSize: '0.85rem', color: '#DC2626', marginBottom: '0.75rem' }} role="alert" aria-live="assertive">{submitError}</p>}
                <button className="c-submit" onClick={submitTouch}>Send Message →</button>
              </div>
            )
          )}

          {tab === 'careers' && (
            sent.careers ? (
              <div style={{ padding: '2rem', background: 'rgba(16,140,88,0.07)', border: '1px solid rgba(16,140,88,0.25)', fontSize: '0.9rem', color: 'var(--navy)', lineHeight: 1.7 }} role="status" aria-live="polite">
                ✓ Application received. We'll review and be in touch.
              </div>
            ) : (
              <div className="c-form">
                <p style={{ fontSize: '0.8rem', color: 'var(--gray)', lineHeight: 1.65, marginBottom: '0.3rem' }}>
                  At Kyro, we foster a culture of growth, collaboration and analytical rigour. We are always looking for talented professionals to join our investment and advisory team.
                </p>
                <div className="c-2col">
                  <input className="c-input" placeholder="Full Name" value={f.cname} onChange={(e) => setF({ ...f, cname: e.target.value })} />
                  <input className="c-input" type="email" placeholder="Email Address" value={f.cemail} onChange={(e) => setF({ ...f, cemail: e.target.value })} />
                </div>
                <input className="c-input" type="tel" placeholder="Phone Number" value={f.cphone} onChange={(e) => setF({ ...f, cphone: e.target.value })} />
                <input className="c-input" placeholder="Role you are applying for" value={f.crole} onChange={(e) => setF({ ...f, crole: e.target.value })} />
                <label style={{ border: '1px dashed var(--gray-lt)', padding: '1.3rem', textAlign: 'center', cursor: 'pointer', background: resumeFile ? 'rgba(16,140,88,0.08)' : 'var(--off)', fontSize: '0.78rem', color: resumeFile ? 'var(--green)' : 'var(--gray)', display: 'block' }}>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                  📎 &nbsp;{resumeFile ? resumeFile.name : 'Upload CV / Resume (PDF, DOC)'}
                </label>
                {submitError && <p style={{ fontSize: '0.85rem', color: '#DC2626', marginBottom: '0.75rem' }} role="alert" aria-live="assertive">{submitError}</p>}
                <button className="c-submit" onClick={submitCareers} disabled={careerSubmitting}>
                  {careerSubmitting ? 'Submitting...' : 'Submit Application →'}
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
